import { createContext, useContext, useEffect, useState } from "react";
import { api, clearWarehouseReadCache } from "../lib/apiClient";
import { hasPermissionFor } from "../lib/permissions";
import { setAppTimezone } from "../lib/timezone";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [dark, setDark] = useState(() => localStorage.getItem("theme") !== "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    clearWarehouseReadCache();
    api.get("/auth/me")
      .then((r) => {
        clearWarehouseReadCache();
        setUser(r.data);
      })
      .catch(() => {
        clearWarehouseReadCache();
        setUser(false);
      });
  }, []);

  useEffect(() => {
    if (user && typeof user === "object" && user.timezone) setAppTimezone(user.timezone);
  }, [user]);

  const login = async (email, password) => {
    clearWarehouseReadCache();
    const { data } = await api.post("/auth/login", { email, password });
    clearWarehouseReadCache();
    setUser(data.user);
  };
  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    return data;
  };
  const googleLogin = async (credential) => {
    clearWarehouseReadCache();
    const { data } = await api.post("/auth/google", { credential });
    clearWarehouseReadCache();
    setUser(data.user);
    return data;
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    clearWarehouseReadCache();
    setUser(false);
  };

  const refreshUser = async () => {
    clearWarehouseReadCache();
    const r = await api.get("/auth/me");
    clearWarehouseReadCache();
    setUser(r.data);
    return r.data;
  };

  const hasPermission = (module, action = "view") => hasPermissionFor(user, module, action);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, googleLogin, logout, refreshUser, hasPermission, dark, setDark }}>
      {children}
    </AuthContext.Provider>
  );
}

// Compatibility scope for legacy pages that still render management controls by
// checking user.role instead of the action-based permission system. The scope is
// local to the wrapped page, while hasPermission keeps using the real user and
// the backend remains authoritative for every create/edit/delete request.
export function LegacyPermissionAdminScope({ module, children }) {
  const context = useContext(AuthContext);
  const user = context?.user;
  if (!context || !user || ["admin", "super_admin"].includes(user.role)) return children;

  const canManage = ["create", "edit", "delete"].some((action) => hasPermissionFor(user, module, action));
  if (!canManage) return children;

  return (
    <AuthContext.Provider value={{ ...context, user: { ...user, role: "admin" } }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
